'use client';

import { useEffect, useSyncExternalStore } from 'react';

import { posthog } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

import { applyPosthogConsent } from './posthog-consent';
import {
  DEFAULT_TELEMETRY_CONSENT_STATE,
  getTelemetryConsentState,
  isEmbeddedFrame,
} from './telemetry-consent';

interface Props {
  token: string;
  apiHost: string;
  children?: React.ReactNode;
}

const noop = () => undefined;
const subscribeNever = () => noop;

export default function PosthogMeasurement({
  token,
  apiHost,
  children,
}: Props) {
  const mounted = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const consentState = isEmbeddedFrame()
        ? DEFAULT_TELEMETRY_CONSENT_STATE
        : getTelemetryConsentState();

      posthog.init(token, {
        api_host: apiHost,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: 'input, textarea, select, div',
        },
        disable_session_recording: true,
        opt_out_capturing_by_default: true,
        opt_out_persistence_by_default: true,
        persistence: 'memory',
        cookieless_mode: 'on_reject',
      });
      applyPosthogConsent(posthog, consentState, {
        purgeIdentity: consentState.hasChoice && !consentState.analytics,
      });
    }
  }, [token, apiHost]);

  if (!mounted) {
    return children;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
